/**
 * Environment configuration
 * Uses environment variables with fallback values
 */

import logger from '../utils/logger';

interface EnvironmentConfig {
  API_BASE_URL: string;
  API_UPLOAD_URL: string;
  SWAGGER_URL: string;
  GRAFANA_URL: string;
  WEBSOCKET_URL: string;
  WEBVIEW_URL: string;
  IMAGE_SERVER_URL: string;
  NODE_ENV: 'development' | 'test' | 'production';
}

// Check for runtime injected config (from Docker/K8s or public/env.js)
const runtimeConfig = (typeof window !== 'undefined' && (window as any).ENV) || {};

// Get current host for dynamic URL generation (safe for SSR/build)
const getCurrentHost = (): string => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.hostname;
  }
  return 'localhost';
};

// Lazy default for API_BASE_URL so we never throw when window isn't ready.
// HTTPS 페이지(예: https://local.luxrobo.net via NGINX Proxy Manager)에서는
// same-origin 으로 호출 → reverse proxy 가 /v0.1, /swagger, /status 를 backend:7000
// 으로 라우팅. Mixed Content + CORS 둘 다 회피.
// HTTP 페이지에서는 기존처럼 :7000 으로 직접 호출 (로컬/사내 LAN 개발).
const getDefaultApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return `http://${getCurrentHost()}:7000`;
};
const getDefaultWebviewUrl = (): string => `${
  typeof window !== 'undefined' && window.location?.protocol === 'https:' ? 'https' : 'http'
}://${getCurrentHost()}:5050`;

// Get config with smart fallback. Accepts both API_BASE_URL and REACT_APP_API_BASE_URL from window.ENV.
const getEnvVar = (key: string, isRequired: boolean = false): string => {
  // 1. Runtime config: support both KEY and REACT_APP_KEY (docker uses KEY, public/env.js may use REACT_APP_KEY)
  const runtimeValue =
    runtimeConfig[key] ||
    (key === 'API_BASE_URL' && runtimeConfig.REACT_APP_API_BASE_URL) ||
    (key === 'WEBVIEW_URL' && runtimeConfig.REACT_APP_WEBVIEW_URL);
  if (runtimeValue) {
    logger.info(`Using runtime config for ${key}: ${runtimeValue}`);
    return runtimeValue;
  }

  // 2. Build-time environment variable
  const envValue = process.env[`REACT_APP_${key}`];
  if (envValue) {
    logger.info(`Using env variable for ${key}: ${envValue}`);
    return envValue;
  }

  // 3. Safe default for API_BASE_URL / WEBVIEW_URL so deployed app never throws
  if (key === 'API_BASE_URL') {
    const def = getDefaultApiBaseUrl();
    logger.warn(`Using default value for ${key}: ${def}`);
    return def;
  }
  if (key === 'WEBVIEW_URL') {
    const def = getDefaultWebviewUrl();
    logger.warn(`Using default value for ${key}: ${def}`);
    return def;
  }

  // 4. If still no value and it's required, throw error
  if (isRequired) {
    logger.error(`Missing required variable: ${key}`);
    throw new Error(`Missing required environment variable: REACT_APP_${key}`);
  }

  return '';
};

// Get optional config with default value
const getOptionalEnvVar = (key: string, defaultValue: string): string => {
  return runtimeConfig[key] || process.env[`REACT_APP_${key}`] || defaultValue;
};

// Get API_BASE_URL with fallback
const apiBaseUrl = getEnvVar('API_BASE_URL');
const webviewUrl = getEnvVar('WEBVIEW_URL');

export const environment: EnvironmentConfig = {
  API_BASE_URL: apiBaseUrl,
  API_UPLOAD_URL: getOptionalEnvVar('API_UPLOAD_URL', `${apiBaseUrl}/v0.1/parking`),
  SWAGGER_URL: getOptionalEnvVar('SWAGGER_URL', `${apiBaseUrl}/swagger/index.html`),
  // GRAFANA_URL: 현장별 SiteProject.dashboard_url 을 우선 사용. 이 환경변수는 fallback.
  // 빈 문자열이면 ProjectDashboardPage 의 Grafana 버튼이 disabled 됨.
  GRAFANA_URL: getOptionalEnvVar('GRAFANA_URL', ''),
  // WebSocket scheme 은 API scheme 을 따라간다 (HTTPS → wss, HTTP → ws).
  // 그렇지 않으면 HTTPS 페이지에서 ws:// 호출이 Mixed Content 로 차단된다.
  WEBSOCKET_URL: getOptionalEnvVar(
    'WEBSOCKET_URL',
    `${apiBaseUrl.startsWith('https://') ? 'wss' : 'ws'}://${apiBaseUrl.replace(/^https?:\/\//, '')}/status`,
  ),
  WEBVIEW_URL: webviewUrl,
  IMAGE_SERVER_URL: getOptionalEnvVar('IMAGE_SERVER_URL', 'http://localhost:5000'),
  NODE_ENV: (process.env.NODE_ENV as 'development' | 'test' | 'production') || 'development',
};

// Development overrides
if (environment.NODE_ENV === 'development') {
  // Keep development URLs as they are
}

// Production overrides
if (environment.NODE_ENV === 'production') {
  // Use environment variables in production
  // Only fallback to default URLs if no environment variables are set
  logger.info('Production mode: Using environment-configured API URLs', {
    API_BASE_URL: environment.API_BASE_URL,
    runtimeConfigPresent: !!runtimeConfig.API_BASE_URL,
    envVarPresent: !!process.env.REACT_APP_API_BASE_URL
  });
}

// Validate URLs
const validateUrl = (url: string, name: string): void => {
  try {
    new URL(url);
  } catch (error) {
    logger.warn(`Invalid ${name} URL: ${url}. Using fallback.`);
  }
};

// Validate all URLs
Object.entries(environment).forEach(([key, value]) => {
  if (key.includes('URL') && typeof value === 'string') {
    validateUrl(value, key);
  }
});

export default environment;